import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const EMPTY_FORM = {
    firstName: "",
    lastName: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    pinCode: "",
    phoneNumber: "",
    countryCode: "",
    stateCode: "",
    isDefault: false,
};

function buildInitialForm(initialValues = {}, countries = []) {
    const firstCountry = countries[0]?.country_code || "";
    const selectedCountryCode = initialValues.countryCode || firstCountry;
    const selectedCountry = countries.find((country) => country.country_code === selectedCountryCode) || countries[0];
    const states = Array.isArray(selectedCountry?.states) ? selectedCountry.states : [];
    const preferredState = states.find((state) => state.state_code === initialValues.stateCode) || states[0];

    return {
        ...EMPTY_FORM,
        firstName: initialValues.firstName || "",
        lastName: initialValues.lastName || "",
        addressLine1: initialValues.addressLine1 || "",
        addressLine2: initialValues.addressLine2 || "",
        city: initialValues.city || "",
        pinCode: initialValues.pinCode || "",
        phoneNumber: initialValues.phoneNumber || "",
        countryCode: selectedCountry?.country_code || "",
        stateCode: preferredState?.state_code || "",
        isDefault: Boolean(initialValues.isDefault),
    };
}

export function AddressForm({
    countries = [],
    initialValues = null,
    onSubmit,
    onCancel,
    isSubmitting = false,
    submitLabel = "Save address",
    hideCountry = false,
    showDefaultOption = true,
}) {
    const [form, setForm] = useState(() => buildInitialForm(initialValues || {}, countries));
    const [attempted, setAttempted] = useState(false);

    useEffect(() => {
        setForm(buildInitialForm(initialValues || {}, countries));
        setAttempted(false);
    }, [initialValues, countries]);

    useEffect(() => {
        if (countries.length === 0) return;

        setForm((prev) => {
            const selectedCountry = countries.find((country) => country.country_code === prev.countryCode) || countries[0];
            const states = Array.isArray(selectedCountry?.states) ? selectedCountry.states : [];
            const nextCountryCode = selectedCountry?.country_code || "";
            const nextStateCode = states.some((state) => state.state_code === prev.stateCode)
                ? prev.stateCode
                : states[0]?.state_code || "";

            if (prev.countryCode === nextCountryCode && prev.stateCode === nextStateCode) return prev;
            return { ...prev, countryCode: nextCountryCode, stateCode: nextStateCode };
        });
    }, [countries]);

    const selectedCountry = useMemo(() => {
        if (countries.length === 0) return null;
        return countries.find((country) => country.country_code === form.countryCode) || countries[0];
    }, [countries, form.countryCode]);

    const statesForCountry = useMemo(() => {
        return Array.isArray(selectedCountry?.states) ? selectedCountry.states : [];
    }, [selectedCountry]);

    const errors = useMemo(() => {
        const v = {};
        if (!form.firstName.trim()) v.firstName = "Required";
        if (!form.lastName.trim()) v.lastName = "Required";
        if (!form.addressLine1.trim()) v.addressLine1 = "Required";
        if (!form.city.trim()) v.city = "Required";
        if (!form.pinCode.trim()) v.pinCode = "Required";
        if (!form.phoneNumber.trim()) v.phoneNumber = "Required";
        if (!hideCountry && !form.countryCode.trim()) v.countryCode = "Required";
        if (!form.stateCode.trim()) v.stateCode = "Required";
        return v;
    }, [form, hideCountry]);

    const patch = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setAttempted(true);
        if (Object.keys(errors).length) return;

        const addressLine2 = form.addressLine2.trim();

        await onSubmit({
            country_code: form.countryCode,
            state_code: form.stateCode,
            first_name: form.firstName.trim(),
            last_name: form.lastName.trim(),
            company: null,
            address_line1: form.addressLine1.trim(),
            address_line2: addressLine2 || null,
            city: form.city.trim(),
            pin_code: form.pinCode.trim(),
            phone_number: form.phoneNumber.trim(),
            is_default: Boolean(form.isDefault),
        });

        setForm((prev) => ({
            ...EMPTY_FORM,
            countryCode: prev.countryCode,
            stateCode: prev.stateCode,
        }));
        setAttempted(false);
    };

    return (
        <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold uppercase tracking-[0.15em]">First Name</Label>
                    <Input
                        value={form.firstName}
                        onChange={(e) => patch("firstName", e.target.value)}
                        className={`mt-2 h-12 rounded-2xl border-2 ${attempted && errors.firstName ? "border-destructive" : "focus:border-primary"}`}
                    />
                </div>
                <div>
                    <Label className="text-xs font-semibold uppercase tracking-[0.15em]">Last Name</Label>
                    <Input
                        value={form.lastName}
                        onChange={(e) => patch("lastName", e.target.value)}
                        className={`mt-2 h-12 rounded-2xl border-2 ${attempted && errors.lastName ? "border-destructive" : "focus:border-primary"}`}
                    />
                </div>
            </div>

            <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.15em]">Address Line 1</Label>
                <Input
                    value={form.addressLine1}
                    onChange={(e) => patch("addressLine1", e.target.value)}
                    className={`mt-2 h-12 rounded-2xl border-2 ${attempted && errors.addressLine1 ? "border-destructive" : "focus:border-primary"}`}
                />
            </div>

            <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.15em]">Address Line 2 (Optional)</Label>
                <Input
                    value={form.addressLine2}
                    onChange={(e) => patch("addressLine2", e.target.value)}
                    className="mt-2 h-12 rounded-2xl border-2 focus:border-primary"
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <Label className="text-xs font-semibold uppercase tracking-[0.15em]">City</Label>
                    <Input
                        value={form.city}
                        onChange={(e) => patch("city", e.target.value)}
                        className={`mt-2 h-12 rounded-2xl border-2 ${attempted && errors.city ? "border-destructive" : "focus:border-primary"}`}
                    />
                </div>
                <div>
                    <Label className="text-xs font-semibold uppercase tracking-[0.15em]">Pincode</Label>
                    <Input
                        value={form.pinCode}
                        onChange={(e) => patch("pinCode", e.target.value)}
                        className={`mt-2 h-12 rounded-2xl border-2 ${attempted && errors.pinCode ? "border-destructive" : "focus:border-primary"}`}
                    />
                </div>
            </div>

            <div>
                <Label className="text-xs font-semibold uppercase tracking-[0.15em]">Phone</Label>
                <Input
                    value={form.phoneNumber}
                    onChange={(e) => patch("phoneNumber", e.target.value.replace(/\D/g, ""))}
                    inputMode="numeric"
                    className={`mt-2 h-12 rounded-2xl border-2 ${attempted && errors.phoneNumber ? "border-destructive" : "focus:border-primary"}`}
                />
            </div>

            <div className={`grid grid-cols-1 ${hideCountry ? "" : "sm:grid-cols-2"} gap-4`}>
                {!hideCountry && (
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-[0.15em]">Country</Label>
                        <Select
                            value={form.countryCode || "none"}
                            onValueChange={(value) => {
                                if (value === "none") return;
                                const selectedCountry = countries.find((c) => c.country_code === value);
                                patch("countryCode", value);
                                patch("stateCode", selectedCountry?.states?.[0]?.state_code || "");
                            }}
                        >
                            <SelectTrigger className={`mt-2 h-12 rounded-2xl border-2 ${attempted && errors.countryCode ? "border-destructive" : "focus:border-primary"}`}>
                                <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                            <SelectContent>
                                {countries.length === 0 ? (
                                    <SelectItem value="none">No countries available</SelectItem>
                                ) : countries.map((country) => (
                                    <SelectItem key={country.country_code} value={country.country_code}>
                                        {country.country_name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}
                <div className={hideCountry ? "sm:col-span-2" : ""}>
                    <Label className="text-xs font-semibold uppercase tracking-[0.15em]">State</Label>
                    <Select
                        value={form.stateCode || "none"}
                        onValueChange={(value) => {
                            if (value === "none") return;
                            patch("stateCode", value);
                        }}
                    >
                        <SelectTrigger className={`mt-2 h-12 rounded-2xl border-2 ${attempted && errors.stateCode ? "border-destructive" : "focus:border-primary"}`}>
                            <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                            {statesForCountry.length === 0 ? (
                                <SelectItem value="none">No states available</SelectItem>
                            ) : statesForCountry.map((state) => (
                                <SelectItem key={state.state_code} value={state.state_code}>
                                    {state.state_name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {showDefaultOption && (
                <label className="flex items-center gap-3 rounded-2xl border border-border/60 bg-background/70 px-4 py-3">
                    <Checkbox
                        checked={form.isDefault}
                        onCheckedChange={(checked) => patch("isDefault", Boolean(checked))}
                    />
                    <span className="text-sm text-foreground">Make this my default address</span>
                </label>
            )}

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                {onCancel && (
                    <Button type="button" variant="outline" onClick={onCancel} className="w-full sm:w-auto h-12 rounded-full">
                        Cancel
                    </Button>
                )}
                <Button type="submit" disabled={isSubmitting} className="w-full h-12 rounded-full sm:w-auto px-6">
                    {isSubmitting ? "Saving..." : submitLabel}
                </Button>
            </div>
        </form>
    );
}
